/**
 * Innenräume der Gebäude am Standort.
 *
 * Jeder Raum ist eine Seitenansicht mit Möbeln und Geräten. Die Figur läuft
 * darin wie auf dem Gelände. An einem Objekt löst man per Klick oder E eine
 * Aktion aus: der Schreibtisch öffnet den Heim-PC, die Racks und Arbeitsplätze
 * führen zur passenden Seite. Am Ausgang geht es zurück zum Gelände.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { HomePc } from '../components/home/HomePc';
import { useGame } from '../store/game';
import { Button, Modal, Select } from '../components/ui';
import { placedIn } from './VorOrt';
import { DeviceArt } from '../components/DeviceArt';
import { PlayerFigure } from '../components/figur/PlayerFigure';
import { loadAppearance, type Appearance } from '../lib/appearance';
import type { World } from '@hosting-sim/core';

const FLOOR_Y = 340;
/** Tiefe im Raum: von der Wand bis zum vorderen Bildrand. */
const DEPTH_MIN = 344;
const DEPTH_MAX = 408;
/** Vergrößerung der Figur: gezeichnet ist sie etwa 66 Einheiten hoch, sie soll 180 cm ≈ 170 Einheiten sein. */
const PLAYER_SCALE = 2.6;
const SPEED = 170;
const REACH = 36;
const EXIT_X = 40;

type Action = 'pc' | { route: string } | { rackId: string } | null;

interface Station {
  id: string;
  label: string;
  x: number;
  kind: 'desk' | 'rack' | 'sofa' | 'table' | 'counter' | 'cabinet';
  action: Action;
  /** Kurzer Hinweis unter dem Objekt. */
  hint?: string;
}

interface Room {
  title: string;
  width: number;
  wall: string;
  floor: string;
  stations: Station[];
}

/** Ein Objekt pro Seite der Verwaltung: Der Raum ersetzt die Seitenleiste. */
function place(items: Omit<Station, 'x'>[], start = 150, gap = 150): Station[] {
  return items.map((item, i) => ({ ...item, x: start + i * gap }));
}

function roomWidth(count: number): number {
  return Math.max(900, 200 + count * 150);
}

function buildRoom(raum: string, racks: { id: string; name: string }[]): Room | null {
  switch (raum) {
    case 'haus': {
      const stations = place([
        { id: 'sofa', label: 'Sofa', kind: 'sofa', action: null },
        { id: 'kueche', label: 'Küche', kind: 'counter', action: null },
        { id: 'pc', label: 'Heim-PC', kind: 'desk', action: 'pc', hint: 'Einkauf, Konsole, Lager, Terminal' },
        { id: 'einstellungen', label: 'Einstellungen', kind: 'cabinet', action: { route: 'settings' } },
        { id: 'handbuch', label: 'Handbuch', kind: 'cabinet', action: { route: 'help' } },
        { id: 'chat', label: 'Chat', kind: 'table', action: { route: 'chat' } },
        { id: 'weltlage', label: 'Weltlage', kind: 'table', action: { route: 'world' }, hint: 'Konjunktur und Märkte' },
        { id: 'fachpresse', label: 'Fachpresse', kind: 'table', action: { route: 'news' }, hint: 'Nachrichten' },
      ]);
      return { title: 'Wohnhaus', width: roomWidth(stations.length), wall: '#e7e2d6', floor: '#a16207', stations };
    }
    case 'rechenzentrum': {
      const rackStations: Station[] = racks.map((r, i) => ({ id: r.id, label: r.name, x: 150 + i * 110, kind: 'rack', action: { rackId: r.id }, hint: 'Rack öffnen · Einbau' }));
      const rest = place(
        [
          { id: 'lager', label: 'Lager', kind: 'cabinet', action: { route: 'vor-ort' }, hint: 'Einbau vor Ort' },
          { id: 'rechenzentren', label: 'Rechenzentren', kind: 'cabinet', action: { route: 'datacenters' } },
          { id: 'server', label: 'Server', kind: 'rack', action: { route: 'servers' } },
          { id: 'vps', label: 'VPS', kind: 'rack', action: { route: 'vps' } },
          { id: 'dedicated', label: 'Dedicated', kind: 'rack', action: { route: 'dedicated' } },
          { id: 'ersatzteile', label: 'Ersatzteile', kind: 'cabinet', action: { route: 'parts' } },
          { id: 'umzuege', label: 'Umzüge', kind: 'cabinet', action: { route: 'migrations' } },
        ],
        150 + racks.length * 110 + 60,
        150,
      );
      const stations = [...rackStations, ...rest];
      return { title: 'Rechenzentrum', width: Math.max(roomWidth(stations.length), 1000), wall: '#cbd5e1', floor: '#475569', stations };
    }
    case 'buero': {
      const stations = place([
        { id: 'leitstand', label: 'Leitstand', kind: 'desk', action: { route: '' }, hint: 'Dashboard' },
        { id: 'kunden', label: 'Kunden', kind: 'desk', action: { route: 'customers' } },
        { id: 'tickets', label: 'Tickets', kind: 'desk', action: { route: 'tickets' } },
        { id: 'produkte', label: 'Produkte', kind: 'desk', action: { route: 'products' } },
        { id: 'rechnungen', label: 'Rechnungen', kind: 'desk', action: { route: 'invoices' } },
        { id: 'finanzen', label: 'Finanzen', kind: 'desk', action: { route: 'finance' } },
        { id: 'personal', label: 'Personal', kind: 'desk', action: { route: 'staff' } },
        { id: 'markt', label: 'Markt', kind: 'desk', action: { route: 'market' } },
        { id: 'angebote', label: 'Angebote', kind: 'desk', action: { route: 'offers' } },
        { id: 'ziele', label: 'Ziele', kind: 'desk', action: { route: 'objectives' } },
        { id: 'chronik', label: 'Chronik', kind: 'desk', action: { route: 'chronicle' } },
        { id: 'forschung', label: 'Forschung', kind: 'desk', action: { route: 'research' } },
        { id: 'ki', label: 'KI-Assistent', kind: 'desk', action: { route: 'advisor' } },
      ]);
      return { title: 'Büro', width: roomWidth(stations.length), wall: '#f5f5f4', floor: '#a8a29e', stations };
    }
    case 'netz': {
      const stations = place([
        { id: 'router', label: 'Router', kind: 'rack', action: { route: 'routers' } },
        { id: 'switches', label: 'Switches', kind: 'rack', action: { route: 'switches' } },
        { id: 'leitungen', label: 'Leitungen', kind: 'cabinet', action: { route: 'links' } },
        { id: 'bgp', label: 'BGP', kind: 'rack', action: { route: 'bgp' } },
        { id: 'ixp', label: 'Peering', kind: 'cabinet', action: { route: 'ixp' } },
        { id: 'ipam', label: 'IP-Adressen', kind: 'cabinet', action: { route: 'ipam' } },
        { id: 'lb', label: 'Loadbalancer', kind: 'rack', action: { route: 'loadbalancer' } },
        { id: 'dns', label: 'DNS', kind: 'cabinet', action: { route: 'services' } },
        { id: 'karte', label: 'Netzkarte', kind: 'desk', action: { route: 'map' } },
        { id: 'monitoring', label: 'Monitoring', kind: 'desk', action: { route: 'monitoring' } },
        { id: 'diagnose', label: 'Diagnose', kind: 'desk', action: { route: 'diagnostics' } },
        { id: 'protokoll', label: 'Protokoll', kind: 'cabinet', action: { route: 'logs' } },
      ]);
      return { title: 'Netzwerkzentrale', width: roomWidth(stations.length), wall: '#cbd5e1', floor: '#334155', stations };
    }
    default:
      return null;
  }
}

export function Innenraum() {
  const { raum = '' } = useParams<{ raum: string }>();
  const world = useGame((s) => s.world);
  const gameId = useGame((s) => s.gameId);
  const navigate = useNavigate();
  const [pcOpen, setPcOpen] = useState(false);
  const appearance: Appearance = useMemo(() => loadAppearance(gameId), [gameId]);
  const [rackId, setRackId] = useState<string | null>(null);

  const racks = useMemo(() => Object.values(world?.racks ?? {}).map((r) => ({ id: r.id, name: r.name })), [world?.racks]);
  const room = useMemo(() => buildRoom(raum, racks), [raum, racks]);

  const [x, setX] = useState(room ? Math.round(room.width / 2) : 300);
  const [facing, setFacing] = useState<1 | -1>(1);
  const [walking, setWalking] = useState(false);
  const [phase, setPhase] = useState(0);

  const xRef = useRef(x);
  const [y, setY] = useState(DEPTH_MAX - 16);
  const yRef = useRef(DEPTH_MAX - 16);
  const keys = useRef(new Set<string>());
  const target = useRef<{ x: number; y?: number; station: Station | null; exit: boolean } | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;
  const pcOpenRef = useRef(pcOpen);
  pcOpenRef.current = pcOpen;
  const gameIdRef = useRef(gameId);
  gameIdRef.current = gameId;

  const clampX = (value: number) => {
    const max = (roomRef.current?.width ?? 900) - 40;
    return Math.min(max, Math.max(40, value));
  };

  /** Aktion an einem Objekt. */
  const act = (station: Station) => {
    const action = station.action;
    if (action === 'pc') setPcOpen(true);
    else if (action && typeof action === 'object' && 'rackId' in action) setRackId(action.rackId);
    else if (action && typeof action === 'object' && 'route' in action && gameIdRef.current) navigate(`/game/${gameIdRef.current}/${action.route}`);
  };
  const actRef = useRef(act);
  actRef.current = act;

  const leave = () => {
    if (gameIdRef.current) navigate(`/game/${gameIdRef.current}/zuhause`);
  };
  const leaveRef = useRef(leave);
  leaveRef.current = leave;

  // Bewegung mit fester Geschwindigkeit.
  useEffect(() => {
    let last = performance.now();
    let ph = 0;
    const frame = (now: number) => {
      const dt = Math.min(0.25, (now - last) / 1000);
      last = now;
      let dx = 0;
      let dy = 0;
      if (!pcOpenRef.current) {
        if (keys.current.has('ArrowLeft') || keys.current.has('a') || keys.current.has('A')) dx -= 1;
        if (keys.current.has('ArrowRight') || keys.current.has('d') || keys.current.has('D')) dx += 1;
        if (keys.current.has('ArrowUp') || keys.current.has('w') || keys.current.has('W')) dy -= 1;
        if (keys.current.has('ArrowDown') || keys.current.has('s') || keys.current.has('S')) dy += 1;
        if (dx === 0 && dy === 0 && target.current) {
          const tx = target.current.x - xRef.current;
          const ty = (target.current.y ?? yRef.current) - yRef.current;
          const dist = Math.hypot(tx, ty);
          if (dist <= 2) {
            const { station, exit } = target.current;
            target.current = null;
            if (exit) leaveRef.current();
            else if (station) actRef.current(station);
          } else {
            dx = tx / dist;
            dy = ty / dist;
          }
        }
      } else {
        target.current = null;
      }
      const moving = dx !== 0 || dy !== 0;
      if (moving) {
        const max = (roomRef.current?.width ?? 900) - 40;
        const len = Math.hypot(dx, dy) || 1;
        xRef.current = Math.min(max, Math.max(40, xRef.current + (dx / len) * SPEED * dt));
        yRef.current = Math.min(DEPTH_MAX, Math.max(DEPTH_MIN, yRef.current + (dy / len) * SPEED * dt));
        ph += dt * 10;
        if (Math.abs(dx) > 0.01) setFacing(dx > 0 ? 1 : -1);
        setX(xRef.current);
        setY(yRef.current);
      }
      setWalking(moving);
      setPhase(moving ? ph : 0);
    };
    const timer = window.setInterval(() => frame(performance.now()), 16);
    return () => window.clearInterval(timer);
  }, []);

  // Tasten: Bewegung halten, E/Enter an Objekten und am Ausgang.
  useEffect(() => {
    const isTyping = (el: EventTarget | null) => {
      const node = el as HTMLElement | null;
      return node?.tagName === 'INPUT' || node?.tagName === 'TEXTAREA' || node?.tagName === 'SELECT' || node?.isContentEditable === true;
    };
    const down = (e: globalThis.KeyboardEvent) => {
      if (isTyping(e.target) || pcOpenRef.current) return;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) e.preventDefault();
      keys.current.add(e.key);
      if (e.key === 'e' || e.key === 'E' || e.key === 'Enter') {
        if (xRef.current <= EXIT_X + REACH) return leaveRef.current();
        const near = nearStation(roomRef.current, xRef.current);
        if (near) actRef.current(near);
      }
    };
    const up = (e: globalThis.KeyboardEvent) => keys.current.delete(e.key);
    const blur = () => keys.current.clear();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  if (!world || !gameId) return null;
  if (!room) {
    return <PageHeader title="Unbekannter Raum" description="Diesen Raum gibt es nicht." />;
  }

  const near = nearStation(room, x);
  const atExit = x <= EXIT_X + REACH;

  const onScene = (e: MouseEvent<SVGSVGElement>) => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const roomX = ((e.clientX - rect.left) / rect.width) * room.width;
    const roomY = ((e.clientY - rect.top) / rect.height) * 420;
    target.current = { x: Math.min(room.width - 40, Math.max(40, roomX)), y: Math.min(DEPTH_MAX, Math.max(DEPTH_MIN, roomY)), station: null, exit: false };
  };
  const onStation = (station: Station) => (e: MouseEvent) => {
    e.stopPropagation();
    if (Math.abs(xRef.current - station.x) <= REACH) act(station);
    else target.current = { x: station.x, station, exit: false };
  };
  const onStationKey = (station: Station) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (Math.abs(xRef.current - station.x) <= REACH) act(station);
      else target.current = { x: station.x, station, exit: false };
    }
  };
  const onExit = (e: MouseEvent) => {
    e.stopPropagation();
    if (xRef.current <= EXIT_X + REACH) leave();
    else target.current = { x: EXIT_X, station: null, exit: true };
  };

  const legSwing = walking ? Math.sin(phase) * 6 : 0;
  /** Vorn im Raum ist die Figur größer, hinten kleiner. */
  const perspective = 0.8 + (0.4 * (y - DEPTH_MIN)) / (DEPTH_MAX - DEPTH_MIN);

  return (
    <>
      <PageHeader title={room.title} description="Mit ← → oder A D laufen. An Objekten per Klick oder E handeln, am Ausgang links zurück zum Gelände." />

      <div className="relative mt-4 overflow-hidden rounded-md border border-slate-700">
        <svg viewBox={`0 0 ${room.width} 420`} className="block h-auto w-full select-none" role="img" aria-label={room.title} onClick={onScene}>
          {/* Wand und Boden */}
          <rect width={room.width} height="420" fill={room.wall} />
          <rect y={FLOOR_Y} width={room.width} height={420 - FLOOR_Y} fill={room.floor} />
          <line x1="0" y1={FLOOR_Y} x2={room.width} y2={FLOOR_Y} stroke="#0f172a" strokeOpacity="0.25" strokeWidth="2" />
          {/* Deckenleuchten */}
          {Array.from({ length: Math.ceil(room.width / 300) }).map((_, i) => (
            <g key={i}>
              <line x1={150 + i * 300} y1="0" x2={150 + i * 300} y2="40" stroke="#334155" strokeWidth="2" />
              <rect x={136 + i * 300} y="40" width="28" height="6" fill="#fef3c7" />
            </g>
          ))}

          {/* Ausgang */}
          <g role="button" tabIndex={0} aria-label="Ausgang zum Gelände" onClick={onExit} className="cursor-pointer">
            <rect x="0" y="130" width="80" height="210" fill="#0f172a" opacity="0.25" />
            <rect x="8" y="140" width="64" height="200" fill="#44403c" stroke="#1c1917" strokeWidth="2" />
            <text x="40" y="122" textAnchor="middle" fontSize="12" fill="#1e293b" fontWeight="600">Ausgang</text>
          </g>

          {/* Objekte */}
          {room.stations.map((station) => (
            <Furniture key={station.id} station={station} onClick={onStation(station)} onKeyDown={onStationKey(station)} />
          ))}

          {/* Spielfigur */}
          <g transform={`translate(${x} ${y}) scale(${facing * PLAYER_SCALE * perspective} ${PLAYER_SCALE * perspective})`} aria-label="Deine Figur">
            <PlayerFigure a={appearance} legSwing={legSwing} />
          </g>
        </svg>

        {(near || atExit) && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded bg-slate-900/90 px-3 py-1.5 text-sm text-slate-100 shadow">
            <kbd className="rounded bg-slate-700 px-1.5 py-0.5 font-mono text-xs">E</kbd>{' '}
            {atExit && !near ? 'Zurück zum Gelände' : `${near!.label}${near!.hint ? ` · ${near!.hint}` : ''}`}
          </div>
        )}
      </div>

      <HomePc open={pcOpen} onClose={() => setPcOpen(false)} gameId={gameId} />
      {rackId && world.racks[rackId as never] && (
        <RackDialog world={world} rackId={rackId} onClose={() => setRackId(null)} />
      )}
    </>
  );
}

function nearStation(room: Room | null, x: number): Station | null {
  if (!room) return null;
  return room.stations.find((s) => Math.abs(s.x - x) <= REACH) ?? null;
}

// ---------------------------------------------------------------------------
// Möbel und Geräte
// ---------------------------------------------------------------------------

function Furniture({
  station,
  onClick,
  onKeyDown,
}: {
  station: Station;
  onClick: (e: MouseEvent) => void;
  onKeyDown: (e: KeyboardEvent) => void;
}) {
  const interactive = station.action !== null;
  const common = {
    role: interactive ? 'button' : undefined,
    tabIndex: interactive ? 0 : undefined,
    onClick: interactive ? onClick : undefined,
    onKeyDown: interactive ? onKeyDown : undefined,
    className: interactive ? 'cursor-pointer focus:outline-none' : undefined,
    'aria-label': interactive ? station.label : undefined,
  };
  const x = station.x;
  switch (station.kind) {
    case 'rack':
      return (
        <g {...common}>
          <rect x={x - 30} y="155" width="60" height="185" fill="#0f172a" stroke="#64748b" strokeWidth="2" />
          {Array.from({ length: 12 }).map((_, i) => (
            <g key={i}>
              <rect x={x - 25} y={162 + i * 14.5} width="50" height="11" fill="#1e293b" stroke="#475569" strokeWidth="0.6" />
              <circle cx={x - 19} cy={167.5 + i * 14.5} r="1.6" fill={i % 3 === 0 ? '#f59e0b' : '#22c55e'} />
            </g>
          ))}
          <text x={x} y="148" textAnchor="middle" fontSize="13" fill="#0f172a" fontWeight="600">{station.label}</text>
        </g>
      );
    case 'desk':
      return (
        <g {...common}>
          <rect x={x - 80} y="270" width="160" height="5" fill="#78350f" />
          <rect x={x - 76} y="275" width="5" height="65" fill="#78350f" />
          <rect x={x + 71} y="275" width="5" height="65" fill="#78350f" />
          <rect x={x - 22} y="222" width="44" height="28" fill="#111827" stroke="#334155" strokeWidth="1.5" />
          <rect x={x - 19} y="225" width="38" height="22" fill="#1e3a5f" />
          <rect x={x - 3} y="250" width="6" height="20" fill="#374151" />
          <rect x={x - 12} y="268" width="24" height="2" fill="#374151" />
          <text x={x} y="302" textAnchor="middle" fontSize="11" fill="#0f172a" fontWeight="600">{station.label}</text>
        </g>
      );
    case 'table':
      return (
        <g {...common}>
          <rect x={x - 60} y="270" width="120" height="5" fill="#78350f" />
          <rect x={x - 54} y="275" width="5" height="65" fill="#78350f" />
          <rect x={x + 49} y="275" width="5" height="65" fill="#78350f" />
          <text x={x} y="302" textAnchor="middle" fontSize="11" fill="#0f172a" fontWeight="600">{station.label}</text>
        </g>
      );
    case 'sofa':
      return (
        <g>
          <rect x={x - 90} y="295" width="180" height="45" rx="8" fill="#57534e" />
          <rect x={x - 90} y="262" width="180" height="40" rx="10" fill="#78716c" />
          <text x={x} y="330" textAnchor="middle" fontSize="11" fill="#f5f5f4">{station.label}</text>
        </g>
      );
    case 'counter':
      return (
        <g>
          <rect x={x - 90} y="255" width="180" height="85" fill="#e7e5e4" stroke="#a8a29e" strokeWidth="2" />
          <rect x={x - 90} y="249" width="180" height="8" fill="#78716c" />
          <text x={x} y="234" textAnchor="middle" fontSize="12" fill="#0f172a" fontWeight="600">{station.label}</text>
        </g>
      );
    case 'cabinet':
    default:
      return (
        <g {...common}>
          <rect x={x - 55} y="160" width="110" height="180" fill="#94a3b8" stroke="#475569" strokeWidth="2" />
          <line x1={x} y1="160" x2={x} y2="340" stroke="#475569" strokeWidth="2" />
          <text x={x} y="150" textAnchor="middle" fontSize="12" fill="#0f172a" fontWeight="600">{station.label}</text>
        </g>
      );
  }
}

// ---------------------------------------------------------------------------
// Rack im Detail: Belegung und Einbau aus dem Lager
// ---------------------------------------------------------------------------

/**
 * Das Rack von innen: jede Höheneinheit mit dem Gerät, das dort steht, oder frei.
 * Auf einen freien Platz kann man eine Lieferung aus dem Lager des Standorts
 * einbauen. Die Prüfung (Platz, Strom, Standort) macht der Kern.
 */
function RackDialog({ world, rackId, onClose }: { world: World; rackId: string; onClose: () => void }) {
  const run = useGame((s) => s.run);
  const rack = world.racks[rackId as never];
  const devices = placedIn(world, rack);
  const stored = Object.values(world.deliveries).filter((d) => d.datacenterId === rack.datacenterId && !d.install);
  const [deliveryId, setDeliveryId] = useState<string>(stored[0]?.id ?? '');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const UNIT = 20;
  const occupant = (unit: number) => devices.find((d) => unit >= d.rackUnit && unit < d.rackUnit + d.heightUnits);
  const usedKw = devices.filter((d) => !d.pending).reduce((sum, d) => sum + d.maxWatt / 1000, 0);

  const install = async (unit: number) => {
    if (!deliveryId) return;
    setBusy(true);
    try {
      const result = await run({ type: 'dc/installDelivery', deliveryId: deliveryId as never, rackId: rackId as never, rackUnit: unit });
      setMessage({ ok: result.ok, text: result.message });
    } finally {
      setBusy(false);
    }
  };

  // Von oben nach unten, wie man vor dem Rack steht.
  const rows = Array.from({ length: rack.units }, (_, i) => rack.units - 1 - i);

  return (
    <Modal open onClose={onClose} title={`${rack.name} · ${rack.units} HE · ${usedKw.toFixed(1)} / ${rack.powerBudgetKw} kW`} wide>
      <div className="grid gap-4 md:grid-cols-[1fr_280px]">
        <div className="overflow-y-auto pl-8 pr-1" style={{ maxHeight: 520 }}>
          <div className="relative mx-auto w-full rounded-md border-x-[10px] border-y-[6px] border-slate-600 bg-slate-950" style={{ height: rack.units * UNIT + 12 }}>
            {/* Höheneinheiten-Nummern links neben dem Schrank */}
            {Array.from({ length: rack.units }, (_, unit) => (
              <span key={`n${unit}`} className="absolute -left-8 w-6 text-right font-mono text-[10px] leading-none text-slate-500" style={{ bottom: unit * UNIT + UNIT / 2 - 5 }}>
                {unit + 1}
              </span>
            ))}
            {/* Freie Höheneinheiten: anklickbar */}
            {Array.from({ length: rack.units }, (_, unit) =>
              occupant(unit) ? null : (
                <button
                  key={`f${unit}`}
                  type="button"
                  disabled={busy || stored.length === 0}
                  onClick={() => void install(unit)}
                  className="group absolute inset-x-0 border-b border-slate-800/80 text-left transition hover:bg-slate-500/20 disabled:cursor-default"
                  style={{ bottom: unit * UNIT, height: UNIT }}
                  aria-label={`Platz ${unit + 1} frei`}
                >
                  {stored.length > 0 && (
                    <span className="invisible px-2 font-mono text-[10px] text-slate-200 group-hover:visible">hier einbauen</span>
                  )}
                </button>
              ),
            )}
            {/* Geräte, von unten bis oben in ihrer Höhe */}
            {devices.map((d) => {
              return (
                <div
                  key={d.id}
                  title={d.name}
                  className={`pointer-events-none absolute inset-x-1 flex items-center gap-2 overflow-hidden rounded-sm px-2 font-mono text-[11px] ${
                    d.pending
                      ? 'border border-dashed border-amber-300 bg-amber-300/10 text-amber-200'
                      : 'border border-slate-500 bg-gradient-to-b from-slate-600 to-slate-700 text-slate-100 shadow-sm'
                  }`}
                  style={{ bottom: d.rackUnit * UNIT + 1, height: d.heightUnits * UNIT - 2 }}
                >
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${d.pending ? 'bg-amber-300' : 'bg-emerald-400'}`} />
                  <span className="truncate">{d.name}{d.pending ? ' · Einbau läuft' : ''}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-500">Platz 1 ist unten. Ein freier Platz wählt, wohin das Gerät mit seiner Unterkante kommt.</p>
        </div>

        <div className="space-y-3 text-sm">
          <div className="text-slate-300">Lager des Standorts</div>
          {stored.length === 0 ? (
            <p className="text-xs text-slate-500">Nichts im Lager. Hardware kaufst du über das Terminal oder den Einkauf im Heim-PC.</p>
          ) : (
            <Select label="Gerät" value={deliveryId} onChange={(e) => setDeliveryId(e.target.value)}>
              {stored.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label} · {d.heightUnits} HE
                </option>
              ))}
            </Select>
          )}
          <p className="text-xs text-slate-500">Auf „hier einbauen“ bei einem freien Platz wird das gewählte Gerät mit seiner Unterkante auf diesen Platz gesetzt. Der Einbau dauert einige Stunden.</p>
          {message && (
            <p className={`text-xs ${message.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{message.text}</p>
          )}
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
